import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { PersonaCardBody } from './PersonaCardBody';

export interface PersonaHoverCardProps {
  persona: any;
  isVisible: boolean;
  /**
   * Optional anchor element used for viewport-aware portal placement.
   * When provided + isVisible, the card renders via createPortal(document.body)
   * with computed position (right of anchor → flip left → fall below). This
   * escapes any clipping ancestor (e.g. the carousel's overflow-x-auto).
   *
   * When omitted, falls back to legacy in-flow `absolute right-0 top-12`
   * behavior — kept for callers that wrap the card in their own positioned
   * container (PersonaConversationSurface).
   */
  anchorRef?: React.RefObject<HTMLElement | null>;
  /**
   * Forwarded to PersonaCardBody — when true, the hover card renders
   * Patterns (✓) + Avoids (⚠) sigil-bullet sections sourced from the
   * persona's character bible. Trailhead picker tiles opt in (operator
   * directive: pros on tiles, deeper bible context on hover).
   */
  showBible?: boolean;
}

const HOVER_CARD_WIDTH_PX = 320;
const HOVER_CARD_GAP_PX = 12;
const HOVER_CARD_VIEWPORT_MARGIN_PX = 8;

interface AnchorPosition {
  top: number;
  left: number;
}

export function computeHoverCardPosition(
  rect: DOMRect,
  viewport: { innerWidth: number; innerHeight: number },
): AnchorPosition {
  const { innerWidth: vw, innerHeight: vh } = viewport;
  // Try right of anchor first (most natural for LTR layouts).
  if (rect.right + HOVER_CARD_GAP_PX + HOVER_CARD_WIDTH_PX <= vw - HOVER_CARD_VIEWPORT_MARGIN_PX) {
    return { top: rect.top, left: rect.right + HOVER_CARD_GAP_PX };
  }
  // Flip left if no room right.
  if (rect.left - HOVER_CARD_GAP_PX - HOVER_CARD_WIDTH_PX >= HOVER_CARD_VIEWPORT_MARGIN_PX) {
    return { top: rect.top, left: rect.left - HOVER_CARD_GAP_PX - HOVER_CARD_WIDTH_PX };
  }
  // Fallback: below anchor, x clamped to viewport.
  const clampedLeft = Math.max(
    HOVER_CARD_VIEWPORT_MARGIN_PX,
    Math.min(rect.left, vw - HOVER_CARD_WIDTH_PX - HOVER_CARD_VIEWPORT_MARGIN_PX),
  );
  // Avoid lint unused-var on vh — we don't currently flip vertically since
  // PersonaCardBody is height-bounded by content (~280px) and most anchors
  // are in the top half of the viewport.
  void vh;
  return { top: rect.bottom + HOVER_CARD_GAP_PX, left: clampedLeft };
}

export const PersonaHoverCard: React.FC<PersonaHoverCardProps> = ({
  persona,
  isVisible,
  anchorRef,
  showBible = false,
}) => {
  const [pos, setPos] = useState<AnchorPosition | null>(null);

  useEffect(() => {
    if (!isVisible || !anchorRef?.current || typeof window === 'undefined') {
      setPos(null);
      return;
    }
    const update = () => {
      const el = anchorRef.current;
      if (!el) return;
      setPos(
        computeHoverCardPosition(el.getBoundingClientRect(), {
          innerWidth: window.innerWidth,
          innerHeight: window.innerHeight,
        }),
      );
    };
    update();
    // Capture-phase scroll catches scrolls from any ancestor (carousel, page).
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isVisible, anchorRef]);

  if (!isVisible || !persona) return null;

  // Portal placement when an anchor is available.
  if (anchorRef && pos && typeof document !== 'undefined') {
    return createPortal(
      <div
        data-testid="persona-hover-card-portal"
        style={{
          position: 'fixed',
          top: pos.top,
          left: pos.left,
          width: HOVER_CARD_WIDTH_PX,
        }}
        className="shadow-xl z-50 transition-opacity duration-200"
      >
        <PersonaCardBody persona={persona} showBible={showBible} />
      </div>,
      document.body,
    );
  }

  // Legacy in-flow positioning — caller owns the positioned ancestor.
  return (
    <div className="absolute right-0 top-12 w-80 shadow-xl z-50 transition-all duration-200 opacity-100 translate-y-0">
      <PersonaCardBody persona={persona} showBible={showBible} />
    </div>
  );
};
